import React, {h, Component} from 'preact';
import {PropTypes} from 'preact-compat';
import './ProceedsCalculator.scss';
import GooglePlaceAutocompleteInput from "../GooglePlaceAutocompleteInput";
import InviteAgentModal from './InviteAgentModal';

export default class ProceedsCalculator extends Component {
    static get propTypes() {
        return {
            stateFullName: PropTypes.string,
            state: PropTypes.string,
            city: PropTypes.string,
            countOfAgents: PropTypes.number,
            kw: PropTypes.string,
            campaign: PropTypes.string,
            realtors: PropTypes.array,
            buyPriceRanges: PropTypes.array
        };
    }

    state = {
        housePrice: 0,
        mortgagePrice: 0,
        homeRepairsPrice: 0,

        agentCommission: '',
        preparationWork: '',
        sellerConcessions: '',
        ownershipCosts: '',
        transferTax: '',

        agentCommissionPrice: 0,
        preparationWorkPrice: 0,
        sellerConcessionsPrice: 0,
        ownershipCostsPrice: 0,
        transferTaxPrice: 0,

        housePriceFormatted: '',
        mortgagePriceFormatted: '',
        homeRepairsPriceFormatted: '',
        agentCommissionPriceFormatted: '',
        preparationWorkPriceFormatted: '',
        sellerConcessionsPriceFormatted: '',
        ownershipCostsPriceFormatted: '',
        transferTaxPriceFormatted: '',

        totalCostsToSell: '',
        netProceeds: '',
        address: '',
        location: '',
        locationError: false,
        addressError: false,
        showAdditional: false,
        isMobile: false
    };

    componentDidMount() {
        document.addEventListener('scroll', this.scrollHandler);
        let distance = this.netCalculatorRef.current.getBoundingClientRect().right;
        this.floatingBlockRef.current.style.right = window.innerWidth - distance;
        this.floatingBlockRef.current.style.top = this.netCalcBoxRef.current.getBoundingClientRect().top;

        this.setState({isMobile: window.innerWidth < 992});
        window.addEventListener('resize', this.windowResizeListener);

        trackEvent('ProceedsCalculator', 'visitorSeenProceedsCalculator', 'visitorSeenProceedsCalculator').withGA();
    }

    floatingBlockRef = React.createRef();
    netCalculatorRef = React.createRef();
    netCalcBoxRef = React.createRef();
    addressFieldRef = React.createRef();
    netProceedsRef = React.createRef();

    numberInput(val) {
        return returnFloatNumbersOnly(val);
    }

    normalizeListPrice = value => {
        if (typeof value === 'number') {
            return value;
        }
        if (!value || typeof value !== 'string') {
            return 0;
        }
        let price = Number(value.replace(/\D+/g, '').replace(/^0/g, ''));
        return price < 50000000 ? price: 50000000;
    };

    denormalizeListPrice = value => {
        if (!value) {
            return '';
        }
        const normalizedValue = this.normalizeListPrice(value).toString();
        return normalizedValue ? '$' + normalizedValue.replace(/\d(?=(\d{3})+$)/g, '$&,') : '';
    };

    normalizeCommission = value => {
        if (typeof value === 'number') {
            return value;
        }
        if (!value || typeof value !== 'string') {
            return 0;
        }
        if (value !== '0%.' && value.startsWith('0') && !value.startsWith('0.')) {
            value = value.substr(1);
        }
        if (!value.includes('%')) {
            value = value.slice(0, -1);
        }

        value = value.replace(/[^\d.]/g, '').replace(/[.](?=.*[.])/g, "");
        return value > 99 ? 99 : value;
    };

    calculateCommissionPrice = (commission, price) => {
        return commission && price
            ? Math.round(commission * price / 100)
            : 0
    }

    setCommissionValueAndPrice = (name) => {
        this.setState(state => {
            let commission = state[name];
            let price = this.calculateCommissionPrice(commission, state.housePrice);

            return {
                [name + 'Price']: price,
                [name + 'PriceFormatted']: price ? this.denormalizeListPrice(price).toString() : ''
            }
        });
    };

    handleCommissionValue(e) {
        this.setState(state => {
            let comm = this.normalizeCommission(e.target.value);
            let price = this.calculateCommissionPrice(comm, state.housePrice);

            return {
                [e.target.name] : comm,
                [e.target.name + 'Price']: price,
                [e.target.name + 'PriceFormatted']: price ? this.denormalizeListPrice(price) : ''
            };
        });
        this.calculateResult();
    }

    calculateResult = () => {
        this.setState(state => {
            let totalCostsToSell = state.agentCommissionPrice
                + state.mortgagePrice
                + state.homeRepairsPrice
                + state.preparationWorkPrice
                + state.sellerConcessionsPrice
                + state.ownershipCostsPrice
                + state.transferTaxPrice;
            return {
                totalCostsToSell: totalCostsToSell ? this.denormalizeListPrice(totalCostsToSell) : '',
                netProceeds: state.housePrice >= totalCostsToSell
                    ? this.denormalizeListPrice((state.housePrice - totalCostsToSell).toString())
                    : 'Commission is too high'
            }
        });
    }

    setPriceValue = ({target: {name, value}}) => {
        this.setState( {
            [name]: this.normalizeListPrice(value),
            [name + 'Formatted']: this.denormalizeListPrice(value),
        })
        this.calculateResult();
    };

    setHousePriceValue = ({target: {value}}) => {
        trackEvent('ProceedsCalculator', 'visitorCalculatedProceeds', 'visitorCalculatedProceeds').withGA();
        this.setState({
            housePrice: this.normalizeListPrice(value),
            housePriceFormatted: this.denormalizeListPrice(value)
        });
        this.setCommissionValueAndPrice('agentCommission');
        this.setCommissionValueAndPrice('preparationWork');
        this.setCommissionValueAndPrice('sellerConcessions');
        this.setCommissionValueAndPrice('ownershipCosts');
        this.setCommissionValueAndPrice('transferTax');
        this.calculateResult();
    };

    handleAddressChange = ({target: {value}}) => {
        this.setState({
            address: value
        })
    }

    onSubmitLocation = e => {
        if (!this.state.location || !/(['a-zA-Z ]+,\s*[a-zA-Z].*|\b\d{5}\b)/.test(this.state.location)) {
            this.setState({locationError: true});
            e.preventDefault();
        } else {
            trackEvent('ProceedsCalculator', 'visitorSubmittedAddress', 'visitorSubmittedAddress').withGA();
        }
    }

    onSubmitAddress = e => {
        if (!this.state.address || !isValidAddress(this.state.address)) {
            this.setState({addressError: true});
            e.preventDefault();
        } else {
            trackEvent('ProceedsCalculator', 'visitorSubmittedAddress', 'visitorSubmittedAddress').withGA();
        }
    }

    renderStars = (avgRating, canHasHalf) => {
        let content = [];
        const avgRatingRounded = canHasHalf ? Math.round(avgRating) : avgRating;
        for (let i = 0; i < avgRatingRounded; i++) {
            content.push(<i className="icon-star-filled"/>);
        }
        return content;
    };

    renderAgent(realtor) {
        let rating = null;
        if (realtor.review_count) {
            rating = (realtor.avg_rating ? realtor.avg_rating : 5).toFixed(1);
        }

        return (
            <div className="agent" key={realtor.realtorId}>
                    <div className="agent-data">
                        <a className="title agent-avatar"
                           href={global.baseUrl + 'real-estate-agent/' + realtor.realtorId + '/' + realtor.nameStr}
                           target="_blank">
                            <img className="agent-icon" src={realtor.filteredPhoto} alt="agent-img"/>
                        </a>
                        <div className="agent-content">
                            <div className="agent-content-data">
                                <a className="title"
                                   href= {global.baseUrl + 'real-estate-agent/' + realtor.realtorId + '/' + realtor.nameStr}
                                   target="_blank">
                                    <h4 className="agent-name">{realtor.name}</h4>
                                </a>
                                <div className="subtitle">{realtor.brokerage}</div>
                            </div>

                            <div className="invite-agent-button "
                                 onClick={() => {
                                     trackEvent('ProceedsCalculator', 'visitorClickedAgent', 'visitorClickedAgent').withGA();
                                     this.showInviteAgentModal(realtor);
                                 }}>
                                <span className="hidden-sm hidden-xs">Get Introduced</span>
                                <span className="visible-sm visible-xs">
                                    <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
                                        <g fill="#3889eb">
                                            <path clip-rule="evenodd" d="m1.45333 1.2c-.13991 0-.25333.11342-.25333.25333v8.53333c0 .13994.11342.25334.25333.25334h1.70667c.33137 0 .6.2686.6.6v1.36l2.45333-1.84c.10386-.0779.23018-.12.36-.12h5.97337c.1399 0 .2533-.1134.2533-.25334v-8.53333c0-.13991-.1134-.25333-.2533-.25333zm-1.45333.25333c0-.802651.650679-1.45333 1.45333-1.45333h11.09337c.8026 0 1.4533.650679 1.4533 1.45333v8.53333c0 .80264-.6507 1.45334-1.4533 1.45334h-5.77337l-3.25333 2.44c-.18181.1364-.42506.1583-.62833.0567-.20327-.1017-.33167-.3094-.33167-.5367v-1.96h-1.10667c-.802651 0-1.45333-.6507-1.45333-1.45334z" fill-rule="evenodd"/>
                                            <path d="m1.2 1.45333c0-.13991.11342-.25333.25333-.25333h11.09337c.1399 0 .2533.11342.2533.25333v8.53333c0 .13994-.1134.25334-.2533.25334h-5.97337c-.12982 0-.25614.0421-.36.12l-2.45333 1.84v-1.36c0-.3314-.26863-.6-.6-.6h-1.70667c-.13991 0-.25333-.1134-.25333-.25334z"/>
                                        </g>
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="agent-stats">
                        {rating && (
                            <div className="agent-rating">
                                <span className="agent-number visible-sm visible-xs">{rating}</span>
                                <span className="stars">
                                    {this.renderStars(rating,true)}
                                </span>
                                <span className="agent-number hidden-sm hidden-xs">{rating}</span>
                                <span className="subtitle">rating</span>
                            </div>
                        )}
                        <div className="agent-experience">
                            <span className="agent-number">{realtor.yearsOfExp}</span>
                            <span className="subtitle">Years of Experience</span>
                        </div>
                    </div>

            </div>
        );
    }

    onClickShowAdditional = () => {
        this.setState({showAdditional: !this.state.showAdditional});

        if (!this.state.showAdditional) {
            this.floatingBlockRef.current.style.top = "auto";
            this.floatingBlockRef.current.style.position = 'unset';
        }
    };

    scrollHandler = () => {
        if (this.state.showAdditional && !this.state.isMobile) {
            const netBcr = this.netProceedsRef.current.getBoundingClientRect();
            const floatingBcr = this.floatingBlockRef.current.getBoundingClientRect();
            let netCalcBoxBcr = this.netCalcBoxRef.current.getBoundingClientRect();

            console.log("floatingBcr.top =" + floatingBcr.top );
            if (floatingBcr.top >= 32 && floatingBcr.bottom <= netBcr.top) {
                console.log("netCalcBoxBcr.top =" + netCalcBoxBcr.top );
                if (netCalcBoxBcr.top > 60) {
                    this.floatingBlockRef.current.style.top = netCalcBoxBcr.top;
                } else {
                    this.floatingBlockRef.current.style.top = 32;
                }
                this.floatingBlockRef.current.style.position = 'fixed'
            } else if (floatingBcr.bottom > netBcr.top) {
                this.floatingBlockRef.current.style.position = 'absolute';
                this.floatingBlockRef.current.style.top = this.state.addressError ? 528 : 585;
            }
        }
    };

    windowResizeListener = () => {
        let isMobile = window.innerWidth < 992;
        this.setState({isMobile: isMobile});
        if (isMobile) {
            this.floatingBlockRef.current.style.top = "auto";
            this.floatingBlockRef.current.style.position = 'unset';
        } else {
            let distance = this.netCalculatorRef.current.getBoundingClientRect().right;
            this.floatingBlockRef.current.style.right = window.innerWidth - distance;
            this.floatingBlockRef.current.style.top = this.netCalcBoxRef.current.getBoundingClientRect().top;
        }
    };

    componentWillUnmount() {
        document.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.windowResizeListener);
    }

    showInviteAgentModal = function(realtor) {
        let $modal = showReactModal(React.createElement(InviteAgentModal, {
            tag: '/re/proceedsCalculator',
            realtorId: realtor.realtorId,
            name: realtor.name,
            cityState: this.props.city + ', ' + this.props.state,
            buyPriceRanges: this.props.buyPriceRanges,
            onModalClose: function() {
                $modal.modal('hide');
            }
        }));
    };

    render() {
        const {
            housePriceFormatted, totalCostsToSell, netProceeds,
            agentCommission, preparationWork, sellerConcessions, ownershipCosts, transferTax,
            agentCommissionPriceFormatted, homeRepairsPriceFormatted, preparationWorkPriceFormatted,
            sellerConcessionsPriceFormatted, ownershipCostsPriceFormatted, transferTaxPriceFormatted,
            mortgagePriceFormatted,
            address, location, locationError, addressError, showAdditional
        } = this.state;
        const {stateFullName, state, countOfAgents, kw, campaign} = this.props;

        return (
            <div className="proceeds-calculator section">
                <div className="section bg-blue">
                    <div ref={this.netCalculatorRef} className="root-container net-calculator">
                        <div>
                            <div className="vertical-line"></div>
                            <h1>Net Proceeds Calculator</h1>
                        </div>
                        <p className="subtitle">How much will I make selling my home? Use this calculator to find out.</p>
                        <div ref={this.netCalcBoxRef}>
                            <div className="calc-box">
                                <div className="calc-box-item">
                                    <h3>House Price</h3>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="housePrice"
                                        value={housePriceFormatted ? housePriceFormatted : '$0'}
                                        onInput={this.setHousePriceValue}
                                    />
                                    <p>Your home’s value depends on how it’s sold.</p>
                                    <a href={global.baseUrl + "home-value-estimator-tool"}>Get a real world valuation in under 2 minutes.</a>
                                </div>
                                <div className="calc-box-item">
                                    <h3>Real estate agent commission</h3>
                                    <input
                                        type="text"
                                        className={"form-control" + (agentCommissionPriceFormatted ? " label-space" : "")}
                                        name="agentCommission"
                                        value={agentCommission ? agentCommission + '%' : '0%'}
                                        onInput={this.handleCommissionValue.bind(this)}
                                    />
                                    <span className="label-text">{agentCommissionPriceFormatted}</span>
                                </div>
                                <div className="calc-box-item">
                                    <h3>How much is left on your mortgage?</h3>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="mortgagePrice"
                                        value={mortgagePriceFormatted ? mortgagePriceFormatted : '$0'}
                                        onInput={this.setPriceValue}
                                    />
                                </div>
                                <div className="calc-box-item additional-box">
                                    <h3 className={showAdditional ? '' : 'hide-additional'}
                                        onClick={this.onClickShowAdditional}>Additional costs to consider</h3>
                                    {showAdditional && (
                                        <div>
                                            <div className="form-control-block">
                                                <p>Home Repairs</p>
                                                <input
                                                    type="text"
                                                    className={"form-control"}
                                                    name="homeRepairsPrice"
                                                    value={homeRepairsPriceFormatted ? homeRepairsPriceFormatted : '$0'}
                                                    onInput={this.setPriceValue}
                                                />
                                            </div>
                                            <div className="form-control-block">
                                                <p>Staging and preparation work</p>
                                                <input
                                                    type="text"
                                                    className={"form-control" + (preparationWorkPriceFormatted ? " label-space" : "")}
                                                    name="preparationWork"
                                                    value={preparationWork ? preparationWork + '%' : '0%'}
                                                    onInput={this.handleCommissionValue.bind(this)}
                                                />
                                                <span className="label-text">{preparationWorkPriceFormatted}</span>
                                            </div>
                                            <div className="form-control-block">
                                                <p>Seller concessions</p>
                                                <input
                                                    type="text"
                                                    className={"form-control" + (sellerConcessionsPriceFormatted ? " label-space" : "")}
                                                    name="sellerConcessions"
                                                    value={sellerConcessions ? sellerConcessions + '%' : '0%'}
                                                    onInput={this.handleCommissionValue.bind(this)}
                                                />
                                                <span className="label-text">{sellerConcessionsPriceFormatted}</span>
                                            </div>
                                            <div className="form-control-block">
                                                <p>Home ownership and overlap costs</p>
                                                <input
                                                    type="text"
                                                    className={"form-control" + (ownershipCostsPriceFormatted ? " label-space" : "")}
                                                    name="ownershipCosts"
                                                    value={ownershipCosts ? ownershipCosts + '%' : '0%'}
                                                    onInput={this.handleCommissionValue.bind(this)}
                                                />
                                                <span className="label-text">{ownershipCostsPriceFormatted}</span>
                                            </div>
                                            <div className="form-control-block">
                                                <p>Title, escrow, notary, and transfer tax</p>
                                                <input
                                                    type="text"
                                                    className={"form-control" + (transferTaxPriceFormatted ? " label-space" : "")}
                                                    name="transferTax"
                                                    value={transferTax ? transferTax + '%' : '0%'}
                                                    onInput={this.handleCommissionValue.bind(this)}
                                                />
                                                <span className="label-text">{transferTaxPriceFormatted}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div ref={this.floatingBlockRef} className="white-boxes initial">
                                <div className="white-box result-box">
                                    <p className="subtitle">{netProceeds ? netProceeds : '$0'}</p> {/*TODO:: INSERT VALUE*/}
                                    <p className="description">Estimated net proceeds if you list with a top real estate agent</p>
                                    <div>
                                        <p className="price-desc bold">Breakdown</p>
                                        <div className="price-content">
                                            <div className="price-block">
                                                <p className="price sale-price">{housePriceFormatted ? housePriceFormatted  : '$0'}</p>
                                                <p className="price-desc">Home sale price</p>
                                            </div>
                                            <img src={global.imageBaseUrl + 'minus-circle-white.svg'} />
                                            <div className="price-block">
                                                <p className="price total-costs">{totalCostsToSell ? totalCostsToSell : '$0'}</p> {/*TODO:: INSERT VALUE*/}
                                                <p className="price-desc">Total costs to sell</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="white-box search-box">
                                    <p className="subtitle">Sell your home faster and for more money</p>
                                    <p className="description">We analyze millions of home sales to find real estate agents that sell homes faster and for more money.</p>

                                    <div>
                                        <form className="search-form" action="create-draft-request2" method="GET" name="addressForm"
                                              onSubmit={this.onSubmitAddress}>
                                            <GooglePlaceAutocompleteInput
                                                id="address"
                                                name="address"
                                                className="search-input form-control form-control-reason reason-input"
                                                placeholder="Enter your address"
                                                value={address}
                                                onChange={({target: {value}}) =>
                                                    this.setState({
                                                        address: value
                                                    })
                                                }
                                                onPlaceChanged={(input, place) => {
                                                    input.value = input.value.replace(/\s*,\s*(USA|United States)$/, '');
                                                    this.setState({
                                                        address: input.value
                                                    })
                                                    $(input).trigger('focusout');
                                                }}
                                            />
                                            <input type="hidden" name="dlink" value="true"/>
                                            <input type="hidden" name="from" value="<s:url/>"/>
                                            {kw && (
                                                <input type="hidden" name="kw" value={kw}/>
                                            )}
                                            {campaign && (
                                                <input type="hidden" name="campaign" value={campaign}/>
                                            )}
                                            <input type="hidden" name="source" value="personaFlow"/>
                                            <input type="hidden" value={true} name="seller"/>
                                            <input type="hidden" name="requestType" value="sell"/>

                                            <button className="btn btn-default search-button">Get Started</button>
                                        </form>
                                        {addressError && (
                                            <p className="error">Please enter a valid full address, e.g. 1 Main St, Los Angeles, CA</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div ref={this.netProceedsRef} className="section bg-white net-proceeds">
                    <div className="root-container">
                        <div>
                            <h2>Understanding the Net Proceeds from Your Home Sale</h2>
                            <p className="desc">Net proceeds are profits you'll walk away with after the sale of your home. Learn more about
                                the home sale calculator line items to understand the true costs of selling a house and your
                                realistic proceeds.</p>
                        </div>
                        <div className="row">
                            <div className="col-xs-12 col-sm-4 net-proceeds-option">
                                <div className="line"/>
                                <h3>Estimated Home Sale Price</h3>
                                <p>We recommend beginning with your Zestimate, Zillow's best estimate of your home's market
                                    value. The Zestimate is based on a blend of valuation methods, with a median error rate
                                    of 3.5%. If your home is in peak selling condition, you can assume the Zestimate is a
                                    strong starting point for your home's market value. If you have already received an
                                    offer at a different sale price, edit the field for the most accurate net proceeds
                                    calculation.</p>
                            </div>
                            <div className="col-xs-12 col-sm-4 net-proceeds-option">
                                <div className="line"/>
                                <h3>Agent Commission</h3>
                                <p>Sellers usually pay both their listing agent's commission and the buyer agent's
                                    commission charges, generally 2-3% of the home sale price per agent. Agent commission
                                    fees are negotiable at the onset of your partnership. However, expect to pay up to 3%
                                    for the buyer's agent whether you plan to sell FSBO or with an agent.</p>
                            </div>
                            {/*TODO:: Remove UHL*/}
                            {/*<div className="col-xs-12 col-sm-4">*/}
                            {/*    <div className="line"/>*/}
                            {/*    <h3>Remaining Mortgage</h3>*/}
                            {/*    <p>The balance you still owe on your home loan is your estimated mortgage balance. Contact*/}
                            {/*        your lender to receive a more accurate loan payoff amount which will include interest,*/}
                            {/*        principal and potentially a prepayment fee. To estimate your remaining mortgage,*/}
                            {/*        calculate your amortization schedule. You can update the balance field in the home sale*/}
                            {/*        calculator.</p>*/}
                            {/*</div>*/}
                        </div>
                    </div>
                </div>
                <div className="section bg-blue local-agents">
                    <div className="root-container">
                        <h2>{countOfAgents} local agents ready to compete for you in {stateFullName}</h2>
                        <div>
                            <div className="agents-block">
                                {this.props.realtors && this.props.realtors.map(realtor => {
                                    return this.renderAgent(
                                        realtor
                                    );
                                })}
                                <a href={global.baseUrl + "top-agents-state/" + state} className="view-top-agents visible-xs visible-sm">View Top Agents in {stateFullName} <i className="icon-arrow-direct-right"></i></a>
                            </div>
                            <div className="address-block">
                                <div className="description">
                                    <p>Like these agents?</p>
                                    <p>They’re ready to compete for you in {stateFullName} </p>
                                </div>
                                <form className="search-form" action="create-draft-request2" method="GET" name="locationForm"
                                      onSubmit={this.onSubmitLocation}>
                                    <GooglePlaceAutocompleteInput
                                        id="location"
                                        name="location"
                                        className="search-input form-control form-control-reason reason-input"
                                        placeholder="City and State or ZIP"
                                        value={location}
                                        autocompleteOptions={{types: ['(cities)']}}
                                        onChange={({target: {value}}) =>
                                            this.setState({
                                                location: value
                                            })
                                        }
                                        onPlaceChanged={(input, place) => {
                                            input.value = input.value.replace(/\s*,\s*(USA|United States)$/, '');
                                            this.setState({
                                                location: input.value
                                            })
                                            $(input).trigger('focusout');
                                        }}
                                    />
                                    <input type="hidden" name="dlink" value="true"/>
                                    <input type="hidden" name="from" value="<s:url/>"/>
                                    {kw && (
                                        <input type="hidden" name="kw" value={kw}/>
                                    )}
                                    {campaign && (
                                        <input type="hidden" name="campaign" value={campaign}/>
                                    )}
                                    <input type="hidden" name="source" value="personaFlow"/>
                                    <input type="hidden" value={true} name="seller"/>
                                    <input type="hidden" name="requestType" value="sell"/>

                                    <button className="btn btn-default search-button">Get Started</button>
                                </form>
                                {locationError && (
                                    <p className="error">Please enter a city and state separated by a comma or a 5-digit ZIP code.</p>
                                )}
                            </div>
                        </div>
                        <a href={global.baseUrl + "top-agents-state/" + state} className="view-top-agents hidden-xs hidden-sm">View Top Agents in {stateFullName} <i className="icon-arrow-direct-right"></i></a>
                    </div>
                </div>
            </div>
        );
    }
}
